<template>
  <TaskModal
    ref="taskModal"
    :visible="visibleActionModal"
    :task="taskForTaskModal"
    :create-mode="false"
    :event="event"
    @ok="save"
    @next-workflow-step="nextWorkflowStep"
    @update:visible="$emit('update:visibleActionModal', $event)"
    @open:ih-order-modal="$emit('open:ihOrderModal', $event)"
  >
    <EventActionFieldRowComponent :label="t('components.TaskModal.changelog')">
      <TaskRevisionList v-if="comments != null" class="mt-2" is-full-list :comments="comments" />
    </EventActionFieldRowComponent>
    <CommentUserForTaskModal v-model="commentUserId" :disabled="event.deletedAt != null" />
    <EventActionFieldRowComponent :label="t('components.TaskModal.comment')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model="comment"
        name="comment"
        :required="isCommentRequired"
      >
        <InputTextArea
          v-bind="inputField"
          id="UpdateTaskModalComment"
          v-model="comment"
          data-testid="UpdateTaskModalComment"
          class="w-100"
          :disabled="event.deletedAt != null"
          :state="isCommentRequired ? valid : null"
          @keydown.meta.enter="save(undefined)"
          @keydown.ctrl.enter="save(undefined)"
        />
      </InputRow>
    </EventActionFieldRowComponent>
  </TaskModal>
  <CreateTaskModal
    v-if="showCreateTaskModal && createTaskModalDefaultTask != null"
    v-model:visible-action-modal="showCreateTaskModal"
    :default-task="createTaskModalDefaultTask"
    :event="event"
    @update:visible-action-modal="visibilityChanged"
  />
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import InputRow from "@/components/shared/form/InputRow.vue";
import { type PropType, ref, computed, provide, inject } from "vue";
import { DateTime } from "luxon";
import { cloneDeep } from "lodash";
import { useStore } from "vuex";
import InputTextArea from "primevue/textarea";
import {
  type ActionListActionFields_Task_Fragment,
  useAddTaskCommentMutation,
  useArchiveEventMutation,
  useUpdateCommonTaskMutation,
  EventType,
  DrawflowItemName,
  type EventListEventFieldsFragment,
} from "@/graphql";
import TaskRevisionList from "../TaskRevisionList.vue";
import TaskModal from "./TaskModal.vue";
import { useRevisions } from "../useRevisions";
import CommentUserForTaskModal from "./CommentUserForTaskModal.vue";
import {
  CommonTaskAdditionalComment,
  TaskModalCommentIsRequiredInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import { type EditTaskType, getMetaFromTask } from "../helper/DefaultTask";
import CreateTaskModal from "@/components/event-action/task/modal/CreateTaskModal.vue";
import EventActionFieldRowComponent from "../../EventActionFieldRowComponent.vue";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  visibleActionModal: {
    type: Boolean,
    default: false,
  },
  task: {
    type: Object as PropType<EditTaskType>,
    required: true,
  },
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
});
const emit = defineEmits(["update:visibleActionModal", "open:ihOrderModal"]);
const store = useStore();

const showCreateTaskModal = ref(false);
const createTaskModalDefaultTask = ref<EditTaskType>();
const commentUserId = ref<number | undefined>();
const comment = ref("");

const isCommentRequired = ref(false);

provide(TaskModalCommentIsRequiredInjectionKey, isCommentRequired);

const taskForTaskModal = ref<typeof props.task>(cloneDeep(props.task));
const { mutate: updateTask, loading: loadingUpdateTask } = useUpdateCommonTaskMutation({});

const { mutate: addCommentMutation, loading: loadingTaskComment } = useAddTaskCommentMutation({});
const { mutate: archiveEvent } = useArchiveEventMutation();

const loading = computed(() => loadingUpdateTask.value || loadingTaskComment.value);

const revisions = computed(() => props.task.Revisions ?? []);
const comments = useRevisions(revisions, true);
const additionalComment = inject(CommonTaskAdditionalComment);

function show() {
  taskForTaskModal.value = cloneDeep(props.task);
  commentUserId.value = store.getters["taskComment/getTaskCommentatorId"];
  comment.value = "";
  emit("update:visibleActionModal", true);
}

defineExpose({
  show,
});

async function save(
  callback?: () => void,
  workflowConnection?: NonNullable<
    ActionListActionFields_Task_Fragment["drawFlowNode"]
  >["connections"][0],
) {
  if (loading.value) {
    return;
  }
  if (!commentUserId.value) {
    throw new Error("Commentator is empty");
  }

  const meta = getMetaFromTask(taskForTaskModal.value?.meta);
  await updateTask({
    taskId: props.task.id!,
    task: {
      description: taskForTaskModal.value.description ?? "",
      status: taskForTaskModal.value.status ?? 1,
      deadline: taskForTaskModal.value.deadline ?? DateTime.now().toISO(),
      assigneeId: taskForTaskModal.value.assigneeId!,
      fileIds: taskForTaskModal.value?.fileIds,
      meta,
    },
    comment: additionalComment?.value ?? comment.value,
    commentUserId: commentUserId.value,
  });
  if (workflowConnection != null) {
    if (
      workflowConnection.inputNode.nodeType === DrawflowItemName.ARCHIVE &&
      props.event.id != null
    ) {
      await archiveEvent(
        {
          id: props.event.id,
          eventType: props.event.__typename as EventType,
        },
        {
          optimisticResponse: {
            archiveEvent: {
              __typename: props.event.__typename as any,
              id: props.event.id,
              deletedAt: DateTime.now().toISO(),
              updatedAt: DateTime.now().toISO(),
            },
          },
        },
      );
    }
  }
  if (additionalComment != null) {
    additionalComment.value = null;
  }
  emit("update:visibleActionModal", false);
  callback?.();
  comment.value = "";
}

async function nextWorkflowStep(
  workflowConnection: NonNullable<
    ActionListActionFields_Task_Fragment["drawFlowNode"]
  >["connections"][0],
  task?: EditTaskType,
) {
  if (workflowConnection.settings.setStatus === true) {
    taskForTaskModal.value.status = workflowConnection.settings.status;
  }
  await save(undefined, workflowConnection);
  await addCommentMutation({
    taskId: taskForTaskModal.value.id!,
    comment: workflowConnection.settings.buttonText,
    commentUserId: commentUserId.value!,
  });
  if (task != null) {
    createTaskModalDefaultTask.value = { ...task };
    showCreateTaskModal.value = true;
  }
}

function visibilityChanged(visible: boolean) {
  if (!visible) {
    createTaskModalDefaultTask.value = undefined;
  }
}

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
